import React, { useEffect, useMemo, useState } from 'react';
import { ContentContainer } from '../../components/ContentContainer/contentContainer';
import { Box, Flex, Grid, Select, Text } from '@chakra-ui/react';
import { useMoralisQuery } from 'react-moralis';
import { Game } from './Game/game';
import { Header } from '../../components/Header/header';
import { FiltersPane } from './FiltersPane/filtersPane';
import Moralis from 'moralis-v1';
import { CarouselControls } from '../../components/CarouselControls/carouselControls';
import { t } from 'i18next';
import usePaginationController from '../../providers/PaginationController/usePaginationController';
import { IGame } from '../../providers/Games/useGames';
import { GameLoader } from 'src/components/GameLoader/gameLoader';
import { Color } from 'src/theme/color';

export enum GamesState {
	LOADING,
	FETCHED,
	EMPTY,
}

enum PriceSort {
	ASCENDING,
	DESCENDING,
	NONE,
}

interface IRangePrice {
	minimum: string;
	maximum: string;
}

interface IRangeDate {
	minimum: Date | null;
	maximum: Date;
}

export interface ILookupData {
	keywords: string;
	platform: string[];
	genre: string[];
	cartridge: string[];
}

export const Games = () => {
	const [state, handleState] = useState<GamesState>(GamesState.LOADING);
	const [games, setGames] = useState<IGame[]>([]);
	const [isLoading, setIsLoading] = useState(true);

	const {
		currentPage,
		advancePage,
		recedePage,
		elementsTotal: gamesTotal,
		initElementsTotal: initGamesTotal,
		elementsPerPage,
	} = usePaginationController(15);

	const [sortCriterion, setSortCriterion] = useState('createdAt');
	const [lookupData, setLookupData] = useState<ILookupData>({ cartridge: [], genre: [], keywords: '', platform: [] });

	const {
		data: queryData,
		isLoading: isQueryDataLoading,
		isFetching: isQueryDataFetching,
	} = useMoralisQuery(
		'Games',
		query => {
			let queryModel = query.equalTo('listed', true);

			if (lookupData.platform.length) {
				queryModel = queryModel.containedIn('platforms', lookupData.platform);
			}
			if (lookupData.genre.length) {
				queryModel = queryModel.containedIn('genres', lookupData.genre);
			}
			if (lookupData.cartridge.length) {
				queryModel = queryModel.containedIn('color', lookupData.cartridge);
			}
			if (lookupData.keywords.length) {
				const object = Moralis.Object.extend('Games');
				const creatorMatchQuery = new Moralis.Query(object).matches(
					'creator',
					new RegExp(`.*${lookupData.keywords}.*`),
					'i'
				);
				const nameMatchQuery = new Moralis.Query(object).matches(
					'name',
					new RegExp(`.*${lookupData.keywords}.*`),
					'i'
				);
				const addressMatchQuery = new Moralis.Query(object).matches(
					'address',
					new RegExp(`.*${lookupData.keywords}.*`),
					'i'
				);

				queryModel = Moralis.Query.and(
					queryModel,
					Moralis.Query.or(creatorMatchQuery, nameMatchQuery, addressMatchQuery)
				);
			}
			queryModel = queryModel
				.descending(sortCriterion)
				.skip((currentPage - 1) * elementsPerPage)
				.limit(elementsPerPage);

			/** Set a total of available games after them having been filtered */
			(async () => {
				if (!isLoading) {
					const queryTotal = await queryModel.count();
					initGamesTotal(queryTotal);
				}
			})();

			return queryModel;
		},
		[lookupData, sortCriterion, currentPage]
	);

	const gameList = useMemo(
		() =>
			games.map((game, index) => (
				<Game
					key={`${game.address}_${index}`}
					data={game}
				/>
			)),
		[games]
	);

	const typeOptions: { [p: number]: () => React.ReactNode } = {
		[GamesState.LOADING]: () => (
			<Grid
				height="770px"
				width="100%"
				gridGap="10px"
				justifyContent="space-between"
				alignItems="center"
				templateColumns="repeat(5, 1fr)"
				templateRows="repeat(3, 1fr)"
			>
				{Array.from(Array(13).keys()).map(id => {
					return <GameLoader key={id} />;
				})}
			</Grid>
		),
		[GamesState.FETCHED]: () => gameList,
		[GamesState.EMPTY]: () => (
			<Text
				height="309px"
				width="100%"
				display="flex"
				alignItems="center"
				justifyContent="center"
			>
				No results
			</Text>
		),
	};

	const connectType = useMemo(() => {
		if (state === GamesState.LOADING) {
			return GamesState.LOADING;
		}
		if (state === GamesState.FETCHED) {
			return GamesState.FETCHED;
		}
		if (state === GamesState.EMPTY) {
			return GamesState.EMPTY;
		}
		return GamesState.LOADING;
	}, [state]);

	useEffect(
		() => setIsLoading(!queryData || isQueryDataLoading || isQueryDataFetching),
		[queryData, isQueryDataLoading, isQueryDataFetching]
	);

	useEffect(() => {
		if (isLoading) {
			handleState(GamesState.LOADING);
		} else {
			const payload = (queryData as any).map((entity: any) => entity.attributes) as IGame[];
			setGames(payload);
			(document as any).getElementById('games').scrollIntoView({
				behavior: 'smooth',
			});
			handleState(GamesState.FETCHED);

			if (!payload.length) {
				handleState(GamesState.EMPTY);
			}
		}
	}, [queryData, isLoading]);

	return (
		<ContentContainer>
			<Flex
				flexDirection="column"
				width="100%"
				gridGap="32px"
				background={Color.BACKGROUND_LIGHT}
				paddingBottom="64px"
				alignItems="center"
			>
				<Header />
				<Flex
					id="games"
					width={{ base: '100%', hashup: 'auto', xl: '1240px' }}
					flexDirection="column"
					gridGap="20px"
					paddingX={{ base: '16px', hashup: '32px', xl: '0' }}
					scrollMarginTop="199px"
				>
					<Flex
						gridGap="43px"
						minWidth="100%"
						alignItems="flex-start"
						flexDirection={{ base: 'column', hashup: 'row' }}
					>
						<Flex
							direction="column"
							gap="16px"
							width="100%"
						>
							<Flex
								alignItems="center"
								justifyContent="space-between"
							>
								<Flex
									fontSize="14px"
									lineHeight="18px"
								>
									<Text color="#878787">{t('library.sort.title').toString()}:&nbsp;</Text>
									<Select
										variant="unstyled"
										minWidth="unset"
										width="unset"
										maxWidth="unset"
										fontSize="inherit"
										cursor="pointer"
										defaultValue="createdAt"
										onChange={event => setSortCriterion(event.target.value)}
									>
										/** TODO: Factor out constants */
										<option value="createdAt">
											{t('library.sort.criterion.release').toString()}
										</option>
										<option value="price">{t('library.sort.criterion.price').toString()}</option>
									</Select>
								</Flex>
								<CarouselControls
									onAdvance={advancePage}
									onRecede={recedePage}
									elements={{ total: gamesTotal, currentPage, perPage: elementsPerPage }}
								/>
							</Flex>
							<Grid
								display={{ base: 'grid', hashup: 'none' }}
								overflowX={{ base: 'scroll', hashup: 'unset' }}
								css={{
									'&::-webkit-scrollbar': {
										width: '0 !important',
									},
									overflow: '-moz-scrollbars-none',
									msOverflowStyle: 'none',
								}}
							>
								<Flex
									flexWrap={{ base: 'nowrap', hashup: 'wrap' }}
									width={{ base: 'auto', hashup: '932px' }}
									gridColumnGap="16px"
									gridRowGap="64px"
									margin={{ base: '0 auto', hashup: '0' }}
									overflowX={{ base: 'scroll', hashup: 'unset' }}
									css={{
										'&::-webkit-scrollbar': {
											width: '0 !important',
										},
										overflow: '-moz-scrollbars-none',
										msOverflowStyle: 'none',
									}}
								>
									{typeOptions[connectType]()}
								</Flex>
							</Grid>
							<Flex
								display={{ base: 'none', hashup: 'flex' }}
								flexWrap={{ base: 'nowrap', hashup: 'wrap' }}
								width={{ base: 'auto', hashup: '932px' }}
								gridColumnGap="8px"
								gridRowGap="32px"
								margin={{ base: '0 auto', hashup: '0' }}
								overflowX={{ base: 'scroll', hashup: 'unset' }}
							>
								{typeOptions[connectType]()}
							</Flex>
						</Flex>
						<Box
							mt={{ base: '0', hashup: '48px' }}
							alignSelf="center"
							width="100%"
						>
							<FiltersPane setLookupData={setLookupData} />
						</Box>
					</Flex>
				</Flex>
			</Flex>
		</ContentContainer>
	);
};
